import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import Card from '../../../components/Card';
import { Button } from 'primereact/button';
import { postApiCall } from '../../../service/postApiCall';
import { getApiCall } from '../../../service/httpCall';
import { API_EXTENSION, API_USERS_PROFILE } from '../../../service/url';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import Toastify styles
import { getUser } from '../../../utilities/setting';
import { USER_ROLES } from '../../../utilities/constant';
import Loader from '../../../components/partials/components/loader';
import { toast } from 'react-toastify';

const CreateExtension = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    
    const [companyCode, setCompanyCode] = useState(''); // Company code from assigned company
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [extension, setExtension] = useState('');
    const [description, setDescription] = useState('');
    const [rolePrefix, setRolePrefix] = useState(''); // Prefix from data.prefix (extension code)
    const [originalExtension, setOriginalExtension] = useState(''); // Store original full extension number for edit mode
    const [buttonDisable, setButtonDisable] = useState(false);
    const [extensionError, setExtensionError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Auto-generate username based on: companycode + extensioncode (2-digit) + extension (as entered, no padding)
    const generateUsername = (companyCode, extensionCode, extensionInput) => {
        if (!extensionCode || !extensionInput) return '';
        // Pad extension code to 2 digits
        const paddedExtensionCode = String(extensionCode).padStart(2, '0');
        // Use extension input as-is (no padding)
        return `${companyCode}${paddedExtensionCode}${extensionInput}`;
    };

    useEffect(() => {
        const fetchCompanyCode = async () => {
            try {
                const data = await getUser();
                if (!data || !data.id) {
                    toast.error("User data not found. Please login again.");
                    return;
                }
                
                // Extension code starts from 01, not 00
                const prefix = data.prefix === null || data.prefix === undefined || data.prefix === 0 ? 1 : Number(data.prefix);
                setRolePrefix(String(prefix).padStart(2, '0')); // Set the 2-digit prefix (extension code), padded to 2 digits (01-99)
                
                // Check if user is a call center admin or team lead
                const isCallCenterAdmin = data?.role === USER_ROLES.CALL_CENTRE_ADMIN || 
                                         data?.role === USER_ROLES.CALL_CENTRE_TEAM_LEAD;
                
                let companyCodeValue = null;
                
                // First, try to get company code from user's own assigned company
                companyCodeValue = data?.assignedCompany?.company_code || 
                                 data?.company?.company_code || 
                                 data?.assigned_company_details?.company_code ||
                                 null;
                
                // If call center admin doesn't have company code, fetch from parent
                if (isCallCenterAdmin && !companyCodeValue) {
                    try {
                        // Fetch current user's full profile to get parent_id
                        const currentUserProfileUrl = API_USERS_PROFILE.replace(':id', data.id);
                        const currentUserProfile = await getApiCall(currentUserProfileUrl, {});
                        
                        const parentId = currentUserProfile?.parent_id || null;
                        
                        if (parentId) {
                            // Fetch parent's profile to get parent's company code
                            const parentProfileUrl = API_USERS_PROFILE.replace(':id', parentId);
                            const parentData = await getApiCall(parentProfileUrl, {});
                            
                            // Get company code from parent's assigned company
                            companyCodeValue = parentData?.assigned_company_details?.company_code ||
                                              parentData?.assignedCompany?.company_code ||
                                              parentData?.company?.company_code ||
                                              null;
                            
                            if (companyCodeValue) {
                                console.log('Using parent company code for call center admin:', companyCodeValue);
                            }
                        }
                    } catch (parentError) {
                        console.error('Error fetching parent user data:', parentError);
                    }
                }
                
                // If still no company code, try from current user's profile
                if (!companyCodeValue) {
                    try {
                        const currentUserProfileUrl = API_USERS_PROFILE.replace(':id', data.id);
                        const currentUserProfile = await getApiCall(currentUserProfileUrl, {});
                        companyCodeValue = currentUserProfile?.assigned_company_details?.company_code ||
                                          currentUserProfile?.assignedCompany?.company_code ||
                                          currentUserProfile?.company?.company_code ||
                                          null;
                    } catch (profileError) {
                        console.error('Error fetching current user profile:', profileError);
                    }
                }
                
                if (companyCodeValue) {
                    // Ensure company code is 4 digits, pad with zeros if needed
                    const paddedCompanyCode = String(companyCodeValue).padStart(4, '0');
                    setCompanyCode(paddedCompanyCode);
                } else {
                    console.warn('Company code not found in user data');
                    toast.error("Company code not found. Please contact administrator.");
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                toast.error("Failed to fetch user data.");
            }
        };
        
        fetchCompanyCode();
    }, []);

    // Update username when extension changes
    useEffect(() => {
        if (!isEditMode && rolePrefix && extension && companyCode) {
            const generatedUsername = generateUsername(companyCode, rolePrefix, extension);
            setUsername(generatedUsername);
        }
    }, [extension, rolePrefix, isEditMode, companyCode]);

    // Fetch extension data if in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            setLoading(true);
            getApiCall(`${API_EXTENSION}/${id}`)
                .then((data) => {
                    console.log('Fetched extension data:', data);
                    
                    if (data) {
                        // Check if extension is allocated - redirect if it is
                        if (data.is_allocated === 1) {
                            toast.error("Cannot edit allocated extension. Only unallocated extensions can be edited.");
                            navigate('/extension');
                            return;
                        }

                        // Set username and description immediately
                        setUsername(data.username || '');
                        setPassword(''); // Don't pre-fill password for security
                        setDescription(data.description || '');
                        
                        // Store original full extension number for edit mode
                        const fullExtension = String(data.extension || '');
                        setOriginalExtension(fullExtension);
                        console.log('Full extension:', fullExtension);
                    } else {
                        console.error('No extension data received');
                        toast.error("No extension data found");
                        navigate('/extension');
                    }
                })
                .catch((error) => {
                    console.error('Error fetching extension:', error);
                    const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch extension data.";
                    toast.error(errorMessage);
                    navigate('/extension');
                })
                .finally(() => setLoading(false));
        }
    }, [isEditMode, id, navigate]);

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        
        // Validate password length (min 4 characters)
        if (value && value.length < 4) {
            setPasswordError('Password must be at least 4 characters long');
        } else {
            setPasswordError('');
        }
    };

    const handleExtensionChange = (e) => {
        const input = e.target.value;

        // Allow empty input while typing
        if (/^[1-4]{0,4}$/.test(input)) {
            setExtension(input);
            setExtensionError(""); // valid
        } else {
            setExtensionError("Only digits 1–4 allowed, max 4 digits.");
        }
    };

    const blockInvalidKeys = (e) => {
        if (["e", "E", "+", "-", "."].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSaveExtension = async () => {
        // Validation
        if (extensionError) {
            toast.error("Please fix the extension error before submitting.");
            return;
        }

        if (!isEditMode && !extension) {
            setExtensionError("Extension is required");
            toast.error("Please enter an extension number.");
            return;
        }

        if (!isEditMode && (!password || password.length < 4)) {
            setPasswordError('Password must be at least 4 characters long');
            toast.error("Password must be at least 4 characters long.");
            return;
        }

        if (!username) {
            toast.error("Username is required.");
            return;
        }

        const payload = {
            username,
            password: password || '',
        };
        
        // Only include description if provided (optional)
        if (description && description.trim()) {
            payload.description = description.trim();
        }
        
        // Extension is only sent for create, not for edit (since it can't be changed)
        if (!isEditMode) {
            if (!companyCode) {
                toast.error("Company code is required. Please refresh the page.");
                return;
            }
            // Calculate full extension: companycode + extensioncode + extension (as entered, no padding)
            // Send as string to preserve length for validation, backend will convert to number for storage
            const fullExtensionStr = `${companyCode}${rolePrefix.padStart(2, '0')}${extension}`;
            payload.extension = fullExtensionStr; // Send as string to preserve leading zeros for validation
        }

        try {
            setButtonDisable(true); // Disable button during API call
            
            if (isEditMode) {
                // Update existing extension
                await postApiCall(`${API_EXTENSION}/${id}`, payload);
                toast.success("Extension updated successfully");
            } else {
                // Create new extension
                await postApiCall(API_EXTENSION, payload);
                toast.success("Extension created successfully");
            }
            setTimeout(() => navigate('/extension'), 3000);
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} extension:`, error);
            const errorMessage = error?.response?.data?.message || error?.message || error || `Failed to ${isEditMode ? 'update' : 'create'} extension.`;
            toast.error(errorMessage);
        } finally {
            setButtonDisable(false); // Re-enable button after API call
        }
    };

    return (
        <>
            <div style={{ marginTop: '20px' }}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <Card.Header className="d-block justify-content-between">
                                <div className="header-title">
                                    <h4 className="card-title">{isEditMode ? 'Edit Extension' : 'Create Extension'}</h4>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loading ? (
                                    <Loader />
                                ) : (
                                    <form>
                                        <Row className="mb-3">
                                            {/* Company Code - Static, non-changeable */}
                                            <Col md="4">
                                                <label htmlFor="companyCode" className="form-label">
                                                    Company Code <span style={{ color: "red", margin: "0 4px" }}>*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="companyCode"
                                                    value={companyCode || 'Loading...'}
                                                    readOnly
                                                    disabled
                                                    className="form-control bg-light extension-readonly-field"
                                                    style={{ cursor: 'not-allowed' }}
                                                />
                                            </Col>

                                            {/* Extension - Combined extension code and input */}
                                            <Col md="4">
                                                <label htmlFor="extension" className="form-label">
                                                    Enter Extension <span style={{ color: "red", margin: "0 4px" }}>*</span>
                                                </label>
                                                <div className="d-flex">
                                                    <span className="input-group-text">{rolePrefix.padStart(2, '0')}</span>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[1-4]{1,4}"
                                                        maxLength={4}
                                                        value={extension} 
                                                        onKeyDown={blockInvalidKeys}
                                                        onChange={handleExtensionChange}
                                                        className={`form-control ${extensionError ? 'is-invalid' : ''}`}
                                                        placeholder="Enter (1-4) Digit Extension"
                                                        required
                                                        disabled={isEditMode}
                                                        readOnly={isEditMode}
                                                    />
                                                </div>
                                                {extensionError && (
                                                    <div className="invalid-feedback">{extensionError}</div>
                                                )}
                                                {isEditMode && (
                                                    <small className="text-muted">Extension number cannot be changed</small>
                                                )}
                                            </Col>

                                            {/* Username - Auto-generated, non-changeable */}
                                            <Col md="4">
                                                <label htmlFor="username" className="form-label">
                                                    Username
                                                </label>
                                                <input
                                                    type="text"
                                                    id="username"
                                                    autoComplete="off"
                                                    value={username}
                                                    readOnly
                                                    disabled={!isEditMode}
                                                    className="form-control bg-light extension-readonly-field"
                                                    style={{ cursor: isEditMode ? 'default' : 'not-allowed' }}
                                                    placeholder="Auto-generated"
                                                />
                                                <small className="text-muted d-block mt-1 fst-italic">
                                                    Company Code + Extension Code + Entered Extension
                                                </small>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">

                                            {/* Password - Min 4 characters */}
                                            <Col md="4">
                                                <label htmlFor="password" className="form-label">
                                                    Password {!isEditMode && <span className="text-danger">*</span>}
                                                </label>
                                                <input
                                                    type="password"
                                                    id="password"
                                                    autoComplete="off"
                                                    value={password}
                                                    onChange={handlePasswordChange}
                                                    className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                                                    placeholder={isEditMode ? "Enter new password (leave blank to keep current)" : "Enter Password (min 4 chars)"}
                                                    required={!isEditMode}
                                                />
                                                {passwordError && (
                                                    <div className="invalid-feedback">{passwordError}</div>
                                                )}
                                            </Col>

                                            {/* Description - Optional */}
                                            <Col md="8">
                                                <label htmlFor="description" className="form-label">
                                                    Description <span className="text-muted">(Optional)</span>
                                                </label>
                                                <textarea
                                                    id="description"
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    className="form-control"
                                                    placeholder="Enter Description"
                                                    rows="3"
                                                />
                                            </Col>
                                        </Row>
                                        <Button
                                            label={isEditMode ? "Update" : "Create"}
                                            onClick={handleSaveExtension}
                                            className="p-button-danger mt-3"
                                            type="button"
                                            disabled={buttonDisable}
                                        >
                                            {buttonDisable && (
                                                <span
                                                    className="spinner-border spinner-border-sm ml-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                            )}
                                        </Button>
                                    </form>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
            {/* <ToastContainer position="top-right" autoClose={3000} /> */}
        </>
    );
};

export default CreateExtension;
